import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchRefurbishedStockApi,
  markStockRefurbishedApi,
  RefurbishedStockQuery,
  RefurbishedStockRecord,
  RefurbishedStockResponse,
} from "../../api/refurbishedStock.api";

interface RefurbishedStockState {
  items: RefurbishedStockRecord[];
  pagination: RefurbishedStockResponse["pagination"];
  queryKey: string;
  listRequestId: string | null;
  listLoading: boolean;
  listError: string | null;
  repairingSerialNumber: string | null;
  repairError: string | null;
}

const initialState: RefurbishedStockState = {
  items: [],
  pagination: { totalPages: 0, totalCount: 0, page: 1, limit: 10 },
  queryKey: "",
  listRequestId: null,
  listLoading: false,
  listError: null,
  repairingSerialNumber: null,
  repairError: null,
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as {
    response?: {
      status?: number;
      data?: { message?: unknown; error?: unknown };
    };
    message?: string;
  };
  const message = apiError.response?.data?.message || apiError.response?.data?.error;
  if (typeof message === "string" && message.trim()) return message.trim();

  if (apiError.response?.status === 400 || apiError.response?.status === 422) {
    return "The refurbished stock request is invalid.";
  }
  if (apiError.response?.status === 404) {
    return "The refurbished stock record could not be found.";
  }
  if (apiError.response?.status === 409) {
    return "This stock has already been dispatched or refurbished.";
  }
  return apiError.message || fallback;
};

const createQueryKey = (query: RefurbishedStockQuery) =>
  JSON.stringify({
    page: query.page,
    limit: query.limit,
    search: query.search || "",
    status: query.status || "",
    warehouseId: query.warehouseId || "",
    productId: query.productId || "",
    dispatched: query.dispatched || "",
    receivedFrom: query.receivedFrom || "",
    receivedTo: query.receivedTo || "",
  });

export const fetchRefurbishedStock = createAsyncThunk<
  RefurbishedStockResponse,
  RefurbishedStockQuery,
  { rejectValue: string }
>(
  "refurbishedStock/fetch",
  async (query, { rejectWithValue }) => {
    try {
      return await fetchRefurbishedStockApi(query);
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to fetch refurbished stock."),
      );
    }
  },
);

export const markStockRefurbished = createAsyncThunk<
  unknown,
  string,
  { rejectValue: string }
>(
  "refurbishedStock/markRefurbished",
  async (serialNumber, { rejectWithValue }) => {
    try {
      return await markStockRefurbishedApi(serialNumber);
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to mark stock as refurbished."),
      );
    }
  },
);

const refurbishedStockSlice = createSlice({
  name: "refurbishedStock",
  initialState,
  reducers: {
    clearRefurbishedStockError(state) {
      state.repairError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRefurbishedStock.pending, (state, action) => {
        const nextQueryKey = createQueryKey(action.meta.arg);
        if (state.queryKey !== nextQueryKey) {
          state.items = [];
          state.pagination = {
            totalPages: 0,
            totalCount: 0,
            page: action.meta.arg.page,
            limit: action.meta.arg.limit,
          };
        }
        state.queryKey = nextQueryKey;
        state.listRequestId = action.meta.requestId;
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchRefurbishedStock.fulfilled, (state, action) => {
        if (
          state.listRequestId !== action.meta.requestId ||
          state.queryKey !== createQueryKey(action.meta.arg)
        ) {
          return;
        }
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
        state.listRequestId = null;
        state.listLoading = false;
        state.listError = null;
      })
      .addCase(fetchRefurbishedStock.rejected, (state, action) => {
        if (
          state.listRequestId !== action.meta.requestId ||
          state.queryKey !== createQueryKey(action.meta.arg)
        ) {
          return;
        }
        state.listRequestId = null;
        state.listLoading = false;
        state.listError =
          action.payload || action.error.message || "Failed to fetch refurbished stock.";
      })
      .addCase(markStockRefurbished.pending, (state, action) => {
        state.repairingSerialNumber = action.meta.arg;
        state.repairError = null;
      })
      .addCase(markStockRefurbished.fulfilled, (state) => {
        state.repairingSerialNumber = null;
        state.repairError = null;
      })
      .addCase(markStockRefurbished.rejected, (state, action) => {
        state.repairingSerialNumber = null;
        state.repairError =
          action.payload ||
          action.error.message ||
          "Failed to mark stock as refurbished.";
      });
  },
});

export const { clearRefurbishedStockError } = refurbishedStockSlice.actions;
export default refurbishedStockSlice.reducer;
